import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { getAreaLocation } from '../../../../store/providerList/selectors';

type MapContextType = {
  location: AreaLocation;
  resetValues: () => void;
  setLocation: Dispatch<SetStateAction<AreaLocation>>;
  setZoomLevel: Dispatch<SetStateAction<number>>;
  zoomLevel: number;
};

export const MapContext = createContext<MapContextType>({} as MapContextType);

type AreaLocation = {
  lat: number;
  lng: number;
};

type MapProviderProps = {
  children: ReactNode;
};

export function MapProvider({ children }: MapProviderProps) {
  const areaLocation = useSelector(getAreaLocation);

  const areaValues = useMemo(
    () => ({
      lat: areaLocation.latitude,
      lng: areaLocation.longitude,
    }),
    [areaLocation],
  );

  const [location, setLocation] = useState<AreaLocation>(areaValues);
  const [zoomLevel, setZoomLevel] = useState(areaLocation.zoomLevel);

  const resetValues = useCallback(() => {
    setLocation(areaValues);
    setZoomLevel(areaLocation.zoomLevel);
  }, [areaLocation.zoomLevel, areaValues]);

  return (
    <MapContext.Provider
      value={{
        location,
        resetValues,
        setLocation,
        setZoomLevel,
        zoomLevel,
      }}
    >
      {children}
    </MapContext.Provider>
  );
}

export const useMapContext = () => useContext(MapContext);
